import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal } from 'react-bootstrap';
import { 
  FaSearch, 
  FaFilter,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
  FaMoneyBillWave,
  FaExclamationTriangle,
  FaDownload,
  FaCalendarAlt,
  FaUpload
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../../../assets/css/admin/dashboard/RefundRequests.css';
import AdminLayout from '../../../../layouts/admin/AdminLayout';

const RefundRequests = () => {
  const [refundRequests, setRefundRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [requestsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // ── Receipt upload states ──
  const [showReceiptUpload, setShowReceiptUpload] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  const fetchRefundRequests = () => {
    setLoading(true);
    axios.post('http://127.0.0.1:8000/api/admin/bookings/viewRefundRequests')
      .then((response) => {
        if (response.data.success) {
          setRefundRequests(response.data.refunds);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchRefundRequests();
  }, []);

  const totalRequests = refundRequests.length;
  const pendingRequests = refundRequests.filter(r => (r.refundStatus || 'pending') === 'pending').length;
  const approvedRequests = refundRequests.filter(r => r.refundStatus === 'approved').length;
  const rejectedRequests = refundRequests.filter(r => r.refundStatus === 'rejected').length;

  const stats = [
    { label: 'Total Requests', value: totalRequests, color: 'purple', icon: '📋' },
    { label: 'Pending', value: pendingRequests, color: 'orange', icon: '⏳' },
    { label: 'Approved', value: approvedRequests, color: 'green', icon: '✅' },
    { label: 'Rejected', value: rejectedRequests, color: 'red', icon: '❌' }
  ];

  const filteredRequests = refundRequests.filter(request => {
    const customerName = request.wecustomer 
      ? `${request.wecustomer.firstName} ${request.wecustomer.lastName}` 
      : '';
    const eventName = request.weevent?.eventName || '';

    const matchesSearch = 
      String(request.id).toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      eventName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (request.cancelReason || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || 
      (request.refundStatus || 'pending').toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstRequest, indexOfLastRequest);
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  // Approve button dabate hi receipt upload form khulega
  const openReceiptUpload = (request) => {
    setSelectedRequest(request);
    setReceiptFile(null);
    setShowReceiptUpload(true);
  };

  const submitApprovalWithReceipt = () => {
    if (!receiptFile) {
      Swal.fire({ icon: 'warning', title: 'Receipt Required', text: 'Please upload a receipt before approving.', confirmButtonColor: '#7fd3c1' });
      return;
    }

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('id', selectedRequest.id);
    formData.append('refundStatus', 'approved');
    formData.append('refundReceipt', receiptFile);

    axios.post('http://127.0.0.1:8000/api/admin/bookings/updateRefundStatus', formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((response) => {
        if (response.data.success) {
          fetchRefundRequests();
          setShowReceiptUpload(false);
          setShowModal(false);
          Swal.fire({
            title: 'Approved!',
            text: 'Refund approved and receipt uploaded successfully.',
            icon: 'success',
            confirmButtonColor: '#7fd3c1'
          });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: 'error', title: 'Error', text: 'Kuch ghalat ho gaya, dobara try karein.', confirmButtonColor: '#7fd3c1' });
      })
      .finally(() => setUploadLoading(false));
  };

  const handleReject = (id) => {
    Swal.fire({
      title: 'Reject Refund?',
      text: 'Are you sure you want to reject this refund request?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Reject',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append('id', id);
        formData.append('refundStatus', 'rejected');

        axios.post('http://127.0.0.1:8000/api/admin/bookings/updateRefundStatus', formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
          .then((response) => {
            if (response.data.success) {
              fetchRefundRequests();
              Swal.fire({
                title: 'Rejected!',
                text: 'Refund request has been rejected.',
                icon: 'error',
                confirmButtonColor: '#7fd3c1'
              });
            }
          })
          .catch((error) => console.log(error));
      }
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0';
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}k`;
    return amount.toString();
  };

  const getStatusBadge = (status) => {
    const s = status || 'pending';
    const statusConfig = {
      'pending': { bg: 'warning', icon: <FaClock />, text: 'Pending' },
      'approved': { bg: 'success', icon: <FaCheckCircle />, text: 'Approved' },
      'rejected': { bg: 'danger', icon: <FaTimesCircle />, text: 'Rejected' }
    };
    return statusConfig[s] || statusConfig['pending'];
  };

  return (
    <AdminLayout>
    <div className="rr-refund-requests">
      <Container fluid>
        <div className="rr-page-header">
          <div className="rr-header-content">
            <h2 className="rr-page-title">Refund Requests</h2>
            <p className="rr-page-subtitle">Manage customer refund applications</p>
          </div>
          <div className="rr-header-actions">
            <Button className="rr-action-btn rr-btn-primary">
              <FaDownload className="rr-btn-icon" /> Export Report
            </Button>
          </div>
        </div>

        <Row className="rr-stats-row g-4 mb-4">
          {stats.map((stat, index) => (
            <Col key={index} xl={3} lg={6} md={6} sm={12}>
              <Card className={`rr-stat-card rr-stat-${stat.color}`}>
                <Card.Body className="rr-stat-body">
                  <div className="rr-stat-icon-wrapper">
                    <span className="rr-stat-icon">{stat.icon}</span>
                  </div>
                  <div className="rr-stat-content">
                    <h3 className="rr-stat-value">{stat.value}</h3>
                    <p className="rr-stat-label">{stat.label}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Card className="rr-content-card">
          <Card.Body className="rr-card-body">
            <div className="rr-filters-section">
              <Row className="g-3">
                <Col lg={8} md={12} sm={12}>
                  <div className="rr-filter-group">
                    <label className="rr-filter-label">
                      <FaSearch className="rr-label-icon" /> Search Refund Request
                    </label>
                    <div className="rr-search-wrapper">
                      <FaSearch className="rr-input-icon" />
                      <Form.Control
                        type="text"
                        placeholder="Search by refund ID, customer, event..."
                        className="rr-search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </Col>

                <Col lg={4} md={12} sm={12}>
                  <div className="rr-filter-group">
                    <label className="rr-filter-label">
                      <FaFilter className="rr-label-icon" /> Filter by Status
                    </label>
                    <div className="rr-select-wrapper">
                      <FaFilter className="rr-input-icon" />
                      <Form.Select
                        className="rr-filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </Form.Select>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="rr-table-wrapper">
              <Table responsive className="rr-table">
                <thead className="rr-table-head">
                  <tr>
                    <th className="rr-th">Booking ID</th>
                    <th className="rr-th">Customer</th>
                    <th className="rr-th">Event</th>
                    <th className="rr-th">Amount</th>
                    <th className="rr-th">Reason</th>
                    <th className="rr-th">Request Date</th>
                    <th className="rr-th">Status</th>
                    <th className="rr-th rr-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody className="rr-table-body">
                  {loading ? (
                    <tr><td colSpan="8" className="rr-no-data">Loading...</td></tr>
                  ) : currentRequests.length > 0 ? (
                    currentRequests.map((request) => (
                      <tr key={request.id} className="rr-table-row">
                        <td className="rr-td rr-td-id">
                          <span className="rr-refund-id">#{request.id}</span>
                        </td>
                        <td className="rr-td">
                          <div className="rr-customer-info">
                            <div className="rr-customer-avatar">
                              {request.wecustomer?.firstName?.charAt(0) || 'U'}
                            </div>
                            <div className="rr-customer-details">
                              <span className="rr-customer-name">
                                {request.wecustomer 
                                  ? `${request.wecustomer.firstName} ${request.wecustomer.lastName}` 
                                  : 'Unknown'}
                              </span>
                              <span className="rr-customer-email">{request.wecustomer?.email}</span>
                            </div>
                          </div>
                        </td>
                        <td className="rr-td rr-td-event">
                          <div className="rr-event-info">
                            <span className="rr-event-name">{request.weevent?.eventName || '—'}</span>
                            <span className="rr-booking-id">Booking: {request.id}</span>
                          </div>
                        </td>
                        <td className="rr-td rr-td-amount">
                          <div className="rr-amount-wrapper">
                            <FaMoneyBillWave className="rr-money-icon" />
                            <span className="rr-amount">{formatCurrency(request.advanceAmount)}</span>
                          </div>
                        </td>
                        <td className="rr-td rr-td-reason">
                          <div className="rr-reason-badge">
                            <FaExclamationTriangle className="rr-reason-icon" />
                            <span className="rr-reason-text">{request.cancelReason}</span>
                          </div>
                        </td>
                        <td className="rr-td rr-td-date">
                          <div className="rr-date-info">
                            <FaCalendarAlt className="rr-date-icon" />
                            <span className="rr-date-text">
                              {request.cancelRequestedAt 
                                ? new Date(request.cancelRequestedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                : '—'}
                            </span>
                          </div>
                        </td>
                        <td className="rr-td">
                          <Badge bg={getStatusBadge(request.refundStatus).bg} className="rr-status-badge">
                            {getStatusBadge(request.refundStatus).icon} {getStatusBadge(request.refundStatus).text}
                          </Badge>
                        </td>
                        <td className="rr-td rr-td-actions">
                          <div className="rr-action-buttons">
                            <button className="rr-icon-btn rr-btn-view" onClick={() => handleViewDetails(request)} title="View Details">
                              <FaEye />
                            </button>
                            {(request.refundStatus || 'pending') === 'pending' && (
                              <>
                                <button className="rr-icon-btn rr-btn-approve" onClick={() => openReceiptUpload(request)} title="Approve">
                                  <FaCheckCircle />
                                </button>
                                <button className="rr-icon-btn rr-btn-reject" onClick={() => handleReject(request.id)} title="Reject">
                                  <FaTimesCircle />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="rr-no-data">
                        <div className="rr-no-data-content">
                          <span className="rr-no-data-icon">💰</span>
                          <p className="rr-no-data-text">No refund requests found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {filteredRequests.length > requestsPerPage && (
              <div className="rr-pagination">
                <Button className="rr-page-btn rr-prev-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                  Previous
                </Button>
                <div className="rr-page-numbers">
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index}
                      className={`rr-page-number ${currentPage === index + 1 ? 'rr-active' : ''}`}
                      onClick={() => setCurrentPage(index + 1)}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                <Button className="rr-page-btn rr-next-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                  Next
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* ── Detail Modal ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="rr-details-modal">
        <Modal.Header closeButton className="rr-modal-header">
          <Modal.Title className="rr-modal-title">Refund Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="rr-modal-body">
          {selectedRequest && (
            <div className="rr-details-content">
              <Row className="g-4">
                <Col md={6}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Booking ID</label>
                    <p className="rr-detail-value">#{selectedRequest.id}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Status</label>
                    <Badge bg={getStatusBadge(selectedRequest.refundStatus).bg} className="rr-status-badge">
                      {getStatusBadge(selectedRequest.refundStatus).icon} {getStatusBadge(selectedRequest.refundStatus).text}
                    </Badge>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Customer Name</label>
                    <p className="rr-detail-value">
                      {selectedRequest.wecustomer 
                        ? `${selectedRequest.wecustomer.firstName} ${selectedRequest.wecustomer.lastName}` 
                        : 'Unknown'}
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Customer Email</label>
                    <p className="rr-detail-value">{selectedRequest.wecustomer?.email}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Event Name</label>
                    <p className="rr-detail-value">{selectedRequest.weevent?.eventName || '—'}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Refund Amount</label>
                    <p className="rr-detail-value rr-amount-highlight">
                      ₨ {Number(selectedRequest.advanceAmount || 0).toLocaleString()}
                    </p>
                  </div>
                </Col>
                <Col md={12}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Reason for Refund</label>
                    <p className="rr-detail-value">{selectedRequest.cancelReason}</p>
                  </div>
                </Col>
                <Col md={12}><hr /></Col>
                <Col md={4}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Bank Name</label>
                    <p className="rr-detail-value">{selectedRequest.refundBankName || '—'}</p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Account Holder</label>
                    <p className="rr-detail-value">{selectedRequest.refundAccountHolder || '—'}</p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Account Number</label>
                    <p className="rr-detail-value">{selectedRequest.refundAccountNumber || '—'}</p>
                  </div>
                </Col>
                <Col md={12}>
                  <div className="rr-detail-item">
                    <label className="rr-detail-label">Request Date</label>
                    <p className="rr-detail-value">
                      {selectedRequest.cancelRequestedAt 
                        ? new Date(selectedRequest.cancelRequestedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
                        : '—'}
                    </p>
                  </div>
                </Col>
                {selectedRequest.refundReceipt && (
                  <Col md={12}>
                    <div className="rr-detail-item">
                      <label className="rr-detail-label">Refund Receipt</label>
                      <p className="rr-detail-value">
                        <a href={`http://127.0.0.1:8000/refund_receipts/${selectedRequest.refundReceipt}`} target="_blank" rel="noreferrer">
                          View Uploaded Receipt
                        </a>
                      </p>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="rr-modal-footer">
          {selectedRequest && (selectedRequest.refundStatus || 'pending') === 'pending' && (
            <>
              <Button className="rr-modal-btn rr-approve-btn" onClick={() => openReceiptUpload(selectedRequest)}>
                <FaCheckCircle /> Approve Refund
              </Button>
              <Button className="rr-modal-btn rr-reject-btn" onClick={() => { handleReject(selectedRequest.id); setShowModal(false); }}>
                <FaTimesCircle /> Reject Refund
              </Button>
            </>
          )}
          <Button className="rr-modal-btn rr-close-btn" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Receipt Upload Modal ── */}
      <Modal show={showReceiptUpload} onHide={() => setShowReceiptUpload(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Refund Receipt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '15px' }}>
            Refund approve karne se pehle receipt/proof upload karein.
          </p>
          <Form.Group>
            <Form.Label><FaUpload className="me-2" />Receipt File</Form.Label>
            <Form.Control
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setReceiptFile(e.target.files[0])}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReceiptUpload(false)}>Cancel</Button>
          <Button className="rr-approve-btn" disabled={uploadLoading} onClick={submitApprovalWithReceipt}>
            {uploadLoading ? 'Uploading...' : 'Approve & Upload'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    </AdminLayout>
  );
};

export default RefundRequests;